/**
 * Collections Page
 *
 * Two-column layout: left = collection list with inline create, right = detail + scope analyzer.
 *
 * Features:
 * - Inline create and rename
 * - Drag-to-reorder items within a collection (HTML5 DnD, no external dependency)
 * - Add Items panel (search + bulk-filter by attribute)
 * - Scope Analyzer: evaluates Gate 3 (scope completeness) per item
 * - "Use as Item Pool" — promotes collection membership to a new Item Pool on a selected track
 *
 * Per canon 07A §3, Gate 3 fires after cluster and frame resolution.
 *
 * INVARIANT: collection_memberships must never be used in session readiness or
 * item pool queries. Promotion to an Item Pool is always explicit via the
 * "Use as Item Pool" action.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FolderOpen,
  Plus,
  Trash2,
  Pencil,
  X,
  Check,
  Search,
  Filter,
  ExternalLink,
  Package,
  Ban,
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  GitBranch,
  ChevronDown,
  ChevronUp,
  BarChart3,
  ListChecks,
  GripVertical,
  ArrowUpRight,
} from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { DiagramCollections } from '../../components/prioritization/RelationshipDiagrams';
import { useBoundSavedReport, useEnsureSavedReports } from '../../hooks/useBoundSavedReport';
import { useSavedReportsStore } from '../../stores/prioritization/savedReportsStore';
import { useCollectionsStore } from '../../stores/prioritization/collectionsStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useUIStore } from '../../stores/prioritization/uiStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { useTrackStore } from '../../stores/prioritization/trackStore';
import { useEnterpriseIdentityStore } from '../../stores/prioritization/enterpriseIdentityStore';
import { SmartRuleSection } from './collections/SmartRuleSection';
import type { Collection } from '../../types/prioritization/collections';
import type { Item } from '../../types/prioritization/lifecycle';
import {
  evaluateItemRelationships,
  type RelationshipEvalContext,
  type RuleOutcome,
  type RuleOutcomeLevel,
} from '../../lib/itemRelationshipRuleEngine';

// ============================================================================
// Scope level helpers
// ============================================================================

const LEVEL_ORDER: RuleOutcomeLevel[] = ['block', 'require_approval', 'warn', 'allow'];

function worstLevel(outcomes: RuleOutcome[]): RuleOutcomeLevel {
  if (outcomes.length === 0) return 'allow';
  return outcomes.reduce<RuleOutcomeLevel>((worst, o) => {
    return LEVEL_ORDER.indexOf(o.level) < LEVEL_ORDER.indexOf(worst) ? o.level : worst;
  }, 'allow');
}

const LEVEL_CHIP: Record<RuleOutcomeLevel, { label: string; icon: React.ElementType; cls: string }> = {
  block:            { label: 'Blocked',  icon: Ban,          cls: 'bg-[var(--coral-dim)] text-[var(--coral)]' },
  require_approval: { label: 'Review',   icon: ShieldAlert,  cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  warn:             { label: 'Warning',  icon: AlertTriangle,cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  allow:            { label: 'Clear',    icon: CheckCircle2, cls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
};

// ============================================================================
// Rename inline input
// ============================================================================

function RenameInput({
  value,
  onSave,
  onCancel,
}: {
  value: string;
  onSave: (v: string) => void;
  onCancel: () => void;
}) {
  const [val, setVal] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="flex items-center gap-1.5 flex-1 min-w-0">
      <input
        ref={ref}
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="flex-1 min-w-0 px-2 py-1 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500"
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave(val.trim());
          if (e.key === 'Escape') onCancel();
        }}
      />
      <button onClick={() => onSave(val.trim())} className="p-1 text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] flex-shrink-0">
        <Check className="h-3.5 w-3.5" />
      </button>
      <button onClick={onCancel} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] flex-shrink-0">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ============================================================================
// Add Items panel (search + bulk-filter)
// ============================================================================

type AddMode = 'search' | 'filter';

const FILTER_ATTRIBUTES = [
  { key: 'itemCategory',    label: 'Category' },
  { key: 'metaCategory',    label: 'Meta-category' },
  { key: 'strategicBucket', label: 'Strategic bucket' },
  { key: 'work_type',       label: 'Work type' },
  { key: 'launch_type',     label: 'Launch type' },
  { key: 'investment_type', label: 'Investment type' },
  { key: 'tags',            label: 'Tag' },
] as const;

type FilterAttrKey = typeof FILTER_ATTRIBUTES[number]['key'];

function getAttrValues(items: Item[], key: FilterAttrKey): string[] {
  const vals = new Set<string>();
  for (const item of items) {
    const raw = (item as Record<string, unknown>)[key];
    if (Array.isArray(raw)) raw.forEach((v) => v && vals.add(String(v)));
    else if (raw) vals.add(String(raw));
  }
  return Array.from(vals).sort();
}

function itemMatchesFilter(item: Item, key: FilterAttrKey, value: string): boolean {
  const raw = (item as Record<string, unknown>)[key];
  if (Array.isArray(raw)) return raw.map(String).includes(value);
  return raw != null && String(raw) === value;
}

function AddItemsPanel({
  existingIds,
  onAdd,
  onAddMany,
  onClose,
}: {
  existingIds: string[];
  onAdd: (itemId: string) => void;
  onAddMany: (itemIds: string[]) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<AddMode>('search');
  const [query, setQuery] = useState('');
  const [filterAttr, setFilterAttr] = useState<FilterAttrKey>('itemCategory');
  const [filterValue, setFilterValue] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const allItems = useItemsStore((s) => s.items);

  const available = useMemo(
    () => allItems.filter((i) => !existingIds.includes(i.id)),
    [allItems, existingIds],
  );

  const searchResults = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    return available
      .filter((i) => i.title?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q))
      .slice(0, 15);
  }, [query, available]);

  const attrValues = useMemo(() => getAttrValues(available, filterAttr), [available, filterAttr]);

  const filterResults = useMemo(() => {
    if (!filterValue) return [];
    return available.filter((i) => itemMatchesFilter(i, filterAttr, filterValue));
  }, [available, filterAttr, filterValue]);

  const allSelected = filterResults.length > 0 && filterResults.every((i) => selected.has(i.id));

  const toggleItem = (id: string) =>
    setSelected((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });

  const toggleAll = () =>
    setSelected(allSelected
      ? new Set([...selected].filter((id) => !filterResults.find((i) => i.id === id)))
      : new Set([...selected, ...filterResults.map((i) => i.id)]));

  const handleAddSelected = () => {
    const ids = [...selected].filter((id) => filterResults.find((i) => i.id === id));
    if (ids.length) { onAddMany(ids); setSelected(new Set()); }
  };

  const handleAttrChange = (key: FilterAttrKey) => {
    setFilterAttr(key); setFilterValue(''); setSelected(new Set());
  };
  const handleValueChange = (val: string) => {
    setFilterValue(val); setSelected(new Set());
  };

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      <div className="px-4 py-3 border-b border-[var(--rule)] flex items-center gap-3">
        <h3 className="text-sm font-semibold text-[var(--text-pri)] flex-1">Add Items</h3>
        <div className="flex items-center gap-1 bg-[var(--hover-tint)] p-0.5 text-xs font-medium">
          <button
            onClick={() => setMode('search')}
            className={`flex items-center gap-1 px-2.5 py-1  transition-colors ${mode === 'search' ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] shadow-sm' : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'}`}
          >
            <Search className="h-3 w-3" /> Search
          </button>
          <button
            onClick={() => setMode('filter')}
            className={`flex items-center gap-1 px-2.5 py-1  transition-colors ${mode === 'filter' ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] shadow-sm' : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'}`}
          >
            <Filter className="h-3 w-3" /> Filter
          </button>
        </div>
        <button onClick={onClose} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      {mode === 'search' && (
        <div className="p-3">
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search items by title…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
            />
          </div>
          {query.trim() && searchResults.length === 0 && (
            <p className="text-xs text-[var(--text-ter)] px-1 py-2">No matching items outside this collection.</p>
          )}
          {searchResults.length > 0 && (
            <div className="divide-y divide-[var(--rule)] max-h-64 overflow-y-auto">
              {searchResults.map((item) => (
                <div key={item.id} className="flex items-center gap-3 py-2 px-1">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--text-pri)] truncate">{item.title}</p>
                    {item.itemCategory && (
                      <span className="text-xs text-[var(--text-ter)] capitalize">{item.itemCategory}</span>
                    )}
                  </div>
                  <button
                    onClick={() => { onAdd(item.id); setQuery(''); }}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors flex-shrink-0"
                  >
                    <Plus className="h-3 w-3" /> Add
                  </button>
                </div>
              ))}
            </div>
          )}
          {!query.trim() && (
            <p className="text-xs text-[var(--text-ter)]">Start typing to search for items to add.</p>
          )}
        </div>
      )}

      {mode === 'filter' && (
        <div className="p-3 space-y-3">
          <div className="flex gap-2">
            <select
              value={filterAttr}
              onChange={(e) => handleAttrChange(e.target.value as FilterAttrKey)}
              className="flex-1 text-sm border border-[var(--rule)] px-2.5 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-[var(--bg-raised)]"
            >
              {FILTER_ATTRIBUTES.map((a) => (
                <option key={a.key} value={a.key}>{a.label}</option>
              ))}
            </select>
            <select
              value={filterValue}
              onChange={(e) => handleValueChange(e.target.value)}
              className="flex-1 text-sm border border-[var(--rule)] px-2.5 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-[var(--bg-raised)]"
            >
              <option value="">— pick a value —</option>
              {attrValues.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>

          {filterValue && filterResults.length === 0 && (
            <p className="text-xs text-[var(--text-ter)] py-1">All matching items are already in this collection.</p>
          )}
          {filterResults.length > 0 && (
            <>
              <div className="flex items-center justify-between py-1 border-b border-[var(--rule)]">
                <label className="flex items-center gap-2 text-xs font-medium text-[var(--text-sec)] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="rounded text-primary-600 focus:ring-primary-500 h-3.5 w-3.5"
                  />
                  {allSelected ? 'Deselect all' : `Select all ${filterResults.length} items`}
                </label>
                <span className="text-xs text-[var(--text-ter)]">{selected.size} selected</span>
              </div>
              <div className="divide-y divide-[var(--rule)] max-h-64 overflow-y-auto">
                {filterResults.map((item) => (
                  <label key={item.id} className="flex items-center gap-3 py-2 px-1 cursor-pointer hover:bg-[var(--surface)] rounded">
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={() => toggleItem(item.id)}
                      className="rounded text-primary-600 focus:ring-primary-500 h-3.5 w-3.5 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--text-pri)] truncate">{item.title}</p>
                      {item.itemCategory && (
                        <span className="text-xs text-[var(--text-ter)] capitalize">{item.itemCategory}</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              <div className="pt-1 flex justify-end">
                <button
                  onClick={handleAddSelected}
                  disabled={selected.size === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ListChecks className="h-4 w-4" />
                  Add {selected.size > 0 ? selected.size : ''} Selected
                </button>
              </div>
            </>
          )}
          {!filterValue && (
            <p className="text-xs text-[var(--text-ter)]">Choose an attribute and value to see matching items.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Promote to Item Pool modal
// ============================================================================

function PromoteToItemPoolModal({
  collection,
  itemCount,
  onConfirm,
  onClose,
  isLoading,
}: {
  collection: Collection;
  itemCount: number;
  onConfirm: (trackId: string) => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  const tracks = useTrackStore((s) => s.tracks);
  const [selectedTrackId, setSelectedTrackId] = useState('');

  const activeTracks = useMemo(
    () => tracks.filter((t) => (t as { status?: string }).status !== 'archived'),
    [tracks],
  );

  // Named missing-input guard (§5)
  const blockingReason: string | null = useMemo(() => {
    if (itemCount === 0) {
      return 'Add items to this collection before promoting it to an item pool.';
    }
    if (activeTracks.length === 0) {
      return 'No Tracks available. Create a Track within a cycle to use this collection as an item pool.';
    }
    return null;
  }, [itemCount, activeTracks.length]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">Use as Item Pool</h2>
          <button onClick={onClose} disabled={isLoading} className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] px-4 py-3 mb-4">
          <p className="text-sm text-[var(--text-sec)] font-medium">{collection.name}</p>
          <p className="text-xs text-[var(--text-sec)] mt-0.5">
            {itemCount > 0
              ? `This will create an Item Pool with ${itemCount} item${itemCount !== 1 ? 's' : ''} on the selected track.`
              : 'This collection has no items.'}
          </p>
        </div>

        {blockingReason ? (
          <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] px-4 py-3 mb-4">
            <p className="text-sm text-[var(--mustard)]">{blockingReason}</p>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
                Select Track <span className="text-[var(--coral)]">*</span>
              </label>
              <select
                value={selectedTrackId}
                onChange={(e) => setSelectedTrackId(e.target.value)}
                className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-indigo-300 focus:border-[var(--slate)] bg-[var(--bg-raised)]"
              >
                <option value="">— choose a Track —</option>
                {activeTracks.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <p className="text-xs text-[var(--text-ter)] mb-5">
              The collection itself will not be changed. Items in the collection remain members of this collection.
            </p>
          </>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors disabled:opacity-50"
          >
            {blockingReason ? 'Close' : 'Cancel'}
          </button>
          {!blockingReason && (
            <button
              onClick={() => selectedTrackId && onConfirm(selectedTrackId)}
              disabled={!selectedTrackId || isLoading}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowUpRight className="h-4 w-4" />
              {isLoading ? 'Creating…' : 'Create Item Pool'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Scope analyzer table with drag-to-reorder
// ============================================================================

interface ItemScopeRow {
  item: Item;
  scopeOutcomes: RuleOutcome[];
  allOutcomes: RuleOutcome[];
}

interface SmartRowControls {
  ruleMatchIds: Set<string>;
  pinnedIds: Set<string>;
  onExclude: (itemId: string) => void;
  onUnpin: (itemId: string) => void;
}

function ScopeAnalyzerTable({
  rows,
  orderedItemIds,
  onRemoveItem,
  onReorder,
  smart,
}: {
  rows: ItemScopeRow[];
  orderedItemIds: string[];
  onRemoveItem: (itemId: string) => void;
  onReorder: (orderedItemIds: string[]) => void;
  /**
   * When set, this collection is smart: every visible row is either a live
   * rule match or a pin (excluded items are already filtered out of `rows`
   * upstream — resolveSmartMembership never returns them). The per-row
   * action becomes context-aware (Exclude vs Un-pin) instead of plain
   * Remove, and dragging to reorder is disabled — rule-derived membership
   * has no persisted order.
   */
  smart?: SmartRowControls;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'scope' | 'all'>('scope');
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const dragItemId = useRef<string | null>(null);

  const orderedRows = useMemo(() => {
    const rowMap = new Map(rows.map((r) => [r.item.id, r]));
    const result: ItemScopeRow[] = [];
    for (const id of orderedItemIds) {
      const row = rowMap.get(id);
      if (row) result.push(row);
    }
    // append any rows not in orderedItemIds (shouldn't happen but defensive)
    for (const row of rows) {
      if (!orderedItemIds.includes(row.item.id)) result.push(row);
    }
    return result;
  }, [rows, orderedItemIds]);

  const blockedCount = orderedRows.filter((r) => worstLevel(r.scopeOutcomes) === 'block').length;
  const warnCount = orderedRows.filter((r) => ['warn', 'require_approval'].includes(worstLevel(r.scopeOutcomes))).length;

  const handleDragStart = useCallback((itemId: string) => {
    dragItemId.current = itemId;
  }, []);

  const handleDrop = useCallback((targetItemId: string) => {
    const sourceId = dragItemId.current;
    if (!sourceId || sourceId === targetItemId) {
      setDragOverId(null);
      return;
    }
    const newOrder = [...orderedItemIds];
    const fromIdx = newOrder.indexOf(sourceId);
    const toIdx = newOrder.indexOf(targetItemId);
    if (fromIdx < 0 || toIdx < 0) { setDragOverId(null); return; }
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, sourceId);
    setDragOverId(null);
    dragItemId.current = null;
    onReorder(newOrder);
  }, [orderedItemIds, onReorder]);

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      <div className="px-4 py-3 border-b border-[var(--rule)] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-[var(--text-ter)]" />
          <h2 className="text-sm font-semibold text-[var(--text-pri)]">
            Scope Analyzer
            <span className="ml-1.5 text-[var(--text-ter)] font-normal">({orderedRows.length} items)</span>
          </h2>
          <span className="text-xs text-[var(--text-ter)]">{smart ? '· live from rule' : '· drag to reorder'}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center border border-[var(--rule)] overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setActiveTab('scope')}
              className={`px-2.5 py-1.5 font-medium transition-colors ${
                activeTab === 'scope' ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)]'
              }`}
            >
              Scope gates
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`px-2.5 py-1.5 font-medium transition-colors border-l border-[var(--rule)] ${
                activeTab === 'all' ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)]'
              }`}
            >
              All gates
            </button>
          </div>
          {blockedCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--coral)]">
              <Ban className="h-3.5 w-3.5" />{blockedCount} blocked
            </span>
          )}
          {warnCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--mustard)]">
              <AlertTriangle className="h-3.5 w-3.5" />{warnCount} flagged
            </span>
          )}
        </div>
      </div>

      {orderedRows.length === 0 ? (
        <div className="py-12 text-center px-6">
          <Package className="h-9 w-9 mx-auto mb-3 text-[var(--text-ter)]" />
          <p className="text-sm font-semibold text-[var(--text-sec)] mb-1">This collection is empty</p>
          <p className="text-xs text-[var(--text-ter)] mb-4">Add items to start analyzing scope and organizing your work.</p>
          {/* onAddItems is wired from the parent via the "Add Items" toolbar button */}
        </div>
      ) : (
        <div className="divide-y divide-[var(--rule)]">
          {orderedRows.map(({ item, scopeOutcomes, allOutcomes }) => {
            const displayOutcomes = activeTab === 'scope' ? scopeOutcomes : allOutcomes;
            const level = worstLevel(displayOutcomes);
            const chip = LEVEL_CHIP[level];
            const ChipIcon = chip.icon;
            const isExpanded = expandedId === item.id;
            const hasIssues = displayOutcomes.length > 0 && level !== 'allow';
            const isDragTarget = dragOverId === item.id;

            const isPinned = smart?.pinnedIds.has(item.id) ?? false;

            return (
              <div
                key={item.id}
                draggable={!smart}
                onDragStart={() => !smart && handleDragStart(item.id)}
                onDragOver={(e) => { if (smart) return; e.preventDefault(); setDragOverId(item.id); }}
                onDragLeave={() => setDragOverId(null)}
                onDrop={() => !smart && handleDrop(item.id)}
                onDragEnd={() => { setDragOverId(null); dragItemId.current = null; }}
                className={`group transition-colors ${isExpanded ? 'bg-[var(--surface)]' : ''} ${isDragTarget ? 'bg-primary-50 border-l-2 border-primary-400' : ''}`}
              >
                <div className={`flex items-center gap-3 px-4 py-2.5 ${!isExpanded && !isDragTarget ? 'hover:bg-[var(--surface)]/60' : ''}`}>
                  {smart ? (
                    <span
                      className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                        isPinned ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
                      }`}
                      title={isPinned ? 'Explicitly pinned — the rule alone would not include this item' : 'Matches the bound rule'}
                    >
                      {isPinned ? 'Pinned' : 'Rule'}
                    </span>
                  ) : (
                    <span className="flex-shrink-0 text-[var(--text-ter)] hover:text-[var(--text-ter)] cursor-grab active:cursor-grabbing">
                      <GripVertical className="h-3.5 w-3.5" />
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={() => hasIssues && setExpandedId(isExpanded ? null : item.id)}
                    className={`flex-shrink-0 p-0.5 rounded transition-colors ${
                      hasIssues ? 'text-[var(--text-ter)] hover:text-[var(--text-sec)] cursor-pointer' : 'text-transparent cursor-default'
                    }`}
                  >
                    {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--text-pri)] truncate">{item.title}</p>
                    {item.description && !isExpanded && (
                      <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">{item.description}</p>
                    )}
                  </div>

                  {item.itemCategory && (
                    <span className="hidden sm:inline-flex text-xs px-2 py-0.5 rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)] capitalize whitespace-nowrap flex-shrink-0">
                      {item.itemCategory}
                    </span>
                  )}

                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${chip.cls}`}>
                    <ChipIcon className="h-3 w-3" />
                    {chip.label}
                    {displayOutcomes.length > 0 && level !== 'allow' && (
                      <span className="opacity-70">({displayOutcomes.length})</span>
                    )}
                  </span>

                  <Link
                    to={`/prioritization/items/${item.id}?tab=relationships`}
                    className="p-1 text-[var(--text-ter)] hover:text-primary-600 flex-shrink-0 transition-colors"
                    title="View relationships"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <GitBranch className="h-3.5 w-3.5" />
                  </Link>
                  <Link
                    to={`/prioritization/items/${item.id}`}
                    className="p-1 text-[var(--text-ter)] hover:text-primary-600 flex-shrink-0 transition-colors"
                    title="View item"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                  {smart ? (
                    isPinned ? (
                      <button
                        onClick={() => smart.onUnpin(item.id)}
                        className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] flex-shrink-0 transition-colors"
                        title="Un-pin — reverts to pure rule evaluation"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => smart.onExclude(item.id)}
                        className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] flex-shrink-0 transition-colors"
                        title="Exclude — removes this item even though the rule matches it"
                      >
                        <Ban className="h-3.5 w-3.5" />
                      </button>
                    )
                  ) : (
                    <button
                      onClick={() => onRemoveItem(item.id)}
                      className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] flex-shrink-0 transition-colors"
                      title="Remove from collection"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                {isExpanded && displayOutcomes.length > 0 && (
                  <div className="px-10 pb-3 space-y-1.5">
                    {displayOutcomes.map((o, idx) => {
                      const oCfg = LEVEL_CHIP[o.level];
                      const OIcon = oCfg.icon;
                      return (
                        <div key={idx} className="flex items-start gap-2.5 bg-[var(--surface)] px-3 py-2">
                          <OIcon className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${
                            o.level === 'block' ? 'text-[var(--coral)]' :
                            o.level === 'require_approval' ? 'text-[var(--mustard)]' :
                            o.level === 'warn' ? 'text-[var(--mustard)]' : 'text-[var(--green)]'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-[var(--text-sec)]">{o.message}</p>
                            <p className="text-xs text-[var(--text-ter)] font-mono mt-0.5">Gate {o.gate} · {o.reason}</p>
                          </div>
                          <Link
                            to={`/prioritization/items/${item.id}?tab=relationships`}
                            className="text-xs text-primary-600 hover:underline flex-shrink-0"
                          >
                            Fix →
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Collection Detail (right panel)
// ============================================================================

function CollectionDetail({
  collection,
  orderedItemIds,
  onRename,
  onDelete,
  onAddItem,
  onRemoveItem,
  onReorder,
  onPromote,
}: {
  collection: Collection;
  orderedItemIds: string[];
  onRename: (name: string) => void;
  onDelete: () => void;
  onAddItem: (itemId: string) => void;
  onRemoveItem: (itemId: string) => void;
  onReorder: (orderedItemIds: string[]) => void;
  onPromote: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [showAddItems, setShowAddItems] = useState(false);

  const allItems = useItemsStore((s) => s.items);
  const allGroups = useItemRelationshipsStore((s) => s.groups);
  const allMemberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const allEdges = useItemRelationshipsStore((s) => s.edges);

  const actorId = useEnterpriseIdentityStore((s) => s.currentUserProfile?.id);
  const getResolvedMembership = useCollectionsStore((s) => s.getResolvedMembership);
  const getDelta = useCollectionsStore((s) => s.getDelta);
  const syncSightings = useCollectionsStore((s) => s.syncSightings);
  const markCollectionViewed = useCollectionsStore((s) => s.markCollectionViewed);
  const pinItem = useCollectionsStore((s) => s.pinItem);
  const excludeItem = useCollectionsStore((s) => s.excludeItem);
  const clearOverride = useCollectionsStore((s) => s.clearOverride);

  // The rule is a saved report, and nothing else on this page loads those:
  // without this, after a reload the rule was not in memory, the collection
  // resolved as static and showed "Not a Smart Collection".
  const { report: ruleReport } = useBoundSavedReport(collection.ruleQueryId);

  // Smart Collections (PRD §4.2): resolved = rule matches ∪ pinned − excluded,
  // evaluated live. Falls back to the plain explicit membership (orderedItemIds,
  // sort_order preserved) for a static collection — resolveSmartMembership
  // returns exactly that when ruleAst is undefined. `ruleReport` is a dep
  // because getResolvedMembership reads it from the store: when the rule
  // arrives (or is edited) the membership has to be recomputed.
  const resolved = useMemo(
    () => getResolvedMembership(collection.id, allItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getResolvedMembership, collection.id, collection.ruleQueryId, ruleReport, allItems],
  );
  const effectiveItemIds = resolved.isSmart ? resolved.itemIds : orderedItemIds;

  // Opportunistic sighting sync — no background job (PRD §4.2). Runs whenever
  // the resolved match set is computed for a smart collection.
  useEffect(() => {
    if (resolved.isSmart) void syncSightings(collection.id, resolved.itemIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolved.itemIds is a fresh array each render; guard on its content via join
  }, [collection.id, resolved.isSmart, resolved.itemIds.join(','), syncSightings]);

  const delta = useMemo(
    () => getDelta(collection.id, allItems),
    [getDelta, collection.id, allItems],
  );

  const memberItems = useMemo(
    () => allItems.filter((i) => effectiveItemIds.includes(i.id)),
    [allItems, effectiveItemIds],
  );

  const scopeRows = useMemo((): ItemScopeRow[] => {
    if (effectiveItemIds.length === 0) return [];
    const scopeSet = new Set(effectiveItemIds);
    return memberItems.map((item) => {
      const ctx: RelationshipEvalContext = {
        itemId: item.id,
        allGroups,
        allMemberships,
        allEdges,
        itemFrames: item.frames ?? [],
        scopeItemIds: scopeSet,
        activeFrameId: null,
        declaredTrackRole: null,
        selectedElaborationChildId: null,
        ringAtTime: 3,
        isCommitmentTime: false,
      };
      const result = evaluateItemRelationships(ctx);
      return {
        item,
        scopeOutcomes: result.outcomes.filter((o) => o.gate === 3),
        allOutcomes: result.outcomes,
      };
    });
  }, [memberItems, allGroups, allMemberships, allEdges, effectiveItemIds]);

  const blockedCount = scopeRows.filter((r) => worstLevel(r.scopeOutcomes) === 'block').length;
  const flaggedCount = scopeRows.filter((r) => ['warn', 'require_approval'].includes(worstLevel(r.scopeOutcomes))).length;
  const clearCount  = scopeRows.filter((r) => worstLevel(r.scopeOutcomes) === 'allow').length;

  const handleAddOrPin = (itemId: string) => {
    if (resolved.isSmart) void pinItem(collection.id, itemId, actorId);
    else onAddItem(itemId);
  };
  const handleAddOrPinMany = (itemIds: string[]) => {
    itemIds.forEach(handleAddOrPin);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-4 py-3 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          {editing ? (
            <RenameInput
              value={collection.name}
              onSave={(v) => { if (v) onRename(v); setEditing(false); }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="text-sm font-semibold text-[var(--text-pri)] truncate">{collection.name}</h3>
              <button
                onClick={() => setEditing(true)}
                className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded transition-colors flex-shrink-0"
                title="Rename"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {collection.description && !editing && (
            <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{collection.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onPromote}
            disabled={effectiveItemIds.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] hover:bg-[var(--slate-dim)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Create an Item Pool from this collection on a selected track"
          >
            <ArrowUpRight className="h-3.5 w-3.5" />
            Use as Item Pool
          </button>
          <button
            onClick={() => setShowAddItems(!showAddItems)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium  transition-colors ${
              showAddItems
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
            title={resolved.isSmart ? 'Pin items the rule doesn’t already match' : undefined}
          >
            <Plus className="h-3.5 w-3.5" />
            {resolved.isSmart ? 'Pin Items' : 'Add Items'}
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
            title="Delete collection"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <SmartRuleSection collection={collection} actorId={actorId} matchCount={resolved.ruleMatchIds.size} />

      {resolved.isSmart && delta.newCount > 0 && (
        <div className="flex items-center justify-between bg-[var(--slate-dim)] border border-[var(--slate)] px-4 py-2.5">
          <p className="text-xs font-medium text-[var(--text-sec)]">
            {delta.newCount} new item{delta.newCount === 1 ? '' : 's'} matched since you last looked
          </p>
          <button
            type="button"
            onClick={() => void markCollectionViewed(collection.id)}
            className="text-[11px] font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
          >
            Mark as seen
          </button>
        </div>
      )}

      {/* Stats strip */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <div className="grid grid-cols-4 divide-x divide-[var(--rule)]">
          <div className="px-4 py-3">
            <p className="text-xs text-[var(--text-ter)]">Items</p>
            <p className="text-base font-semibold text-[var(--text-pri)] tabular-nums">{effectiveItemIds.length}</p>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-[var(--text-ter)]">Blocked</p>
            <p className={`text-base font-semibold tabular-nums ${blockedCount > 0 ? 'text-[var(--coral)]' : 'text-[var(--text-ter)]'}`}>{blockedCount}</p>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-[var(--text-ter)]">Flagged</p>
            <p className={`text-base font-semibold tabular-nums ${flaggedCount > 0 ? 'text-[var(--mustard)]' : 'text-[var(--text-ter)]'}`}>{flaggedCount}</p>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-[var(--text-ter)]">Clear</p>
            <p className={`text-base font-semibold tabular-nums ${clearCount > 0 ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>{clearCount}</p>
          </div>
        </div>
      </div>

      {showAddItems && (
        <AddItemsPanel
          existingIds={effectiveItemIds}
          onAdd={handleAddOrPin}
          onAddMany={handleAddOrPinMany}
          onClose={() => setShowAddItems(false)}
        />
      )}

      <ScopeAnalyzerTable
        rows={scopeRows}
        orderedItemIds={effectiveItemIds}
        onRemoveItem={onRemoveItem}
        onReorder={onReorder}
        smart={
          resolved.isSmart
            ? {
                ruleMatchIds: resolved.ruleMatchIds,
                pinnedIds: new Set(effectiveItemIds.filter((id) => !resolved.ruleMatchIds.has(id))),
                onExclude: (itemId) => void excludeItem(collection.id, itemId, actorId),
                onUnpin: (itemId) => void clearOverride(collection.id, itemId),
              }
            : undefined
        }
      />
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function CollectionsPage() {
  const { collectionId: urlCollectionId } = useParams<{ collectionId?: string }>();
  const collections = useCollectionsStore((s) => s.collections);
  const memberships = useCollectionsStore((s) => s.memberships);
  const fetchCollections = useCollectionsStore((s) => s.fetchFromSupabase);
  const createCollection = useCollectionsStore((s) => s.createCollection);
  const updateCollection = useCollectionsStore((s) => s.updateCollection);
  const deleteCollection = useCollectionsStore((s) => s.deleteCollection);
  const addItemToCollection = useCollectionsStore((s) => s.addItemToCollection);
  const removeItemFromCollection = useCollectionsStore((s) => s.removeItemFromCollection);
  const reorderItems = useCollectionsStore((s) => s.reorderItems);
  const promoteToItemPool = useCollectionsStore((s) => s.promoteToItemPool);
  const getOrderedItemIds = useCollectionsStore((s) => s.getOrderedItemIdsForCollection);
  const getResolvedMembership = useCollectionsStore((s) => s.getResolvedMembership);
  const allItems = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const fetchTracks = useTrackStore((s) => s.fetchAllTracks);
  const addNotification = useUIStore((s) => s.addNotification);

  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [pendingDeleteCollection, setPendingDeleteCollection] = useState<Collection | null>(null);
  const createInputRef = useRef<HTMLInputElement>(null);
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);

  useEffect(() => {
    fetchCollections();
    fetchItems();
    fetchRelationships();
    fetchTracks();
  }, [fetchCollections, fetchItems, fetchRelationships, fetchTracks]);

  // Auto-select: prefer URL param, then first collection
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (collections.length === 0) { hasAutoSelectedRef.current = false; return; }
    if (hasAutoSelectedRef.current) return;
    hasAutoSelectedRef.current = true;
    if (urlCollectionId && collections.some((c) => c.id === urlCollectionId)) {
      setActiveCollectionId(urlCollectionId);
    } else {
      setActiveCollectionId(collections[0].id);
    }
  }, [collections, urlCollectionId]);

  useEffect(() => {
    if (isCreating) { setTimeout(() => createInputRef.current?.focus(), 50); }
  }, [isCreating]);

  const activeCollection = collections.find((c) => c.id === activeCollectionId) ?? null;

  // Per-collection item count for left panel badge
  // A Smart Collection's count is its RESOLVED membership (rule ∪ pinned −
  // excluded), not its explicit rows — counting rows listed every Smart
  // Collection as "0 items · empty". The rules are saved reports, loaded here
  // if missing; `savedReports` is a dep so counts settle when they arrive.
  useEnsureSavedReports(collections.map((c) => c.ruleQueryId));
  const savedReports = useSavedReportsStore((s) => s.reports);
  const itemCountByCollection = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of collections) {
      map.set(
        c.id,
        c.ruleQueryId
          ? getResolvedMembership(c.id, allItems).itemIds.length
          : memberships.filter((m) => m.collectionId === c.id).length,
      );
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections, memberships, savedReports, allItems, getResolvedMembership]);

  // Ordered item ids for active collection (respects sort_order). Static
  // collections only — a Smart Collection's live count is resolved inside
  // CollectionDetail; this is used for the promote-confirmation modal's
  // display count, which is why it needs the resolved figure too, below.
  const activeOrderedItemIds = useMemo(
    () => (activeCollectionId ? getOrderedItemIds(activeCollectionId) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeCollectionId, memberships],
  );

  // Resolved (rule ∪ pinned − excluded) count for the promote-confirmation
  // modal — for a static collection this equals activeOrderedItemIds; for a
  // Smart Collection it's the live match count, not the raw explicit rows.
  const activeResolvedItemCount = useMemo(() => {
    if (!activeCollectionId) return 0;
    return getResolvedMembership(activeCollectionId, allItems).itemIds.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCollectionId, allItems, memberships, savedReports]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setNewName('');
    setIsCreating(false);
    const created = await createCollection(name);
    if (created) {
      setActiveCollectionId(created.id);
      addNotification({ type: 'success', title: 'Collection created', message: `"${name}" is ready`, duration: 3000 });
    }
  };

  const handleDelete = (collection: Collection) => {
    setPendingDeleteCollection(collection);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteCollection) return;
    const collection = pendingDeleteCollection;
    setPendingDeleteCollection(null);
    await deleteCollection(collection.id);
    if (activeCollectionId === collection.id) {
      const remaining = collections.filter((c) => c.id !== collection.id);
      setActiveCollectionId(remaining.length > 0 ? remaining[0].id : null);
    }
    addNotification({ type: 'success', title: 'Collection deleted', message: `"${collection.name}" removed`, duration: 3000 });
  };

  const handleAddItem = async (itemId: string) => {
    if (!activeCollectionId) return;
    await addItemToCollection(activeCollectionId, itemId);
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!activeCollectionId) return;
    await removeItemFromCollection(activeCollectionId, itemId);
  };

  const handleReorder = async (orderedIds: string[]) => {
    if (!activeCollectionId) return;
    await reorderItems(activeCollectionId, orderedIds);
  };

  const handlePromote = async (trackId: string) => {
    if (!activeCollectionId) return;
    setIsPromoting(true);
    try {
      const { getSafeSupabaseClient } = await import('../../lib/supabaseSync');
      const supabase = getSafeSupabaseClient();
      const actorId = supabase
        ? (await supabase.auth.getUser()).data.user?.id ?? 'system'
        : 'system';
      const poolId = await promoteToItemPool(activeCollectionId, trackId, actorId);
      if (poolId) {
        setPromoteModalOpen(false);
        addNotification({
          type: 'success',
          title: 'Item Pool created',
          message: `A new Item Pool was made from "${activeCollection?.name}". The collection is unchanged.`,
          duration: 4000,
        });
      } else {
        addNotification({ type: 'error', title: 'Promotion failed', message: 'Could not create Item Pool. Check console for details.', duration: 5000 });
      }
    } finally {
      setIsPromoting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<FolderOpen className="w-4 h-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Collections"
        subtitle="Named groups for thematic organization and scope analysis"
        actions={
          <button
            onClick={() => setIsCreating(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Collection
          </button>
        }
      />

      {/* Full-page zero state when no collections exist */}
      {collections.length === 0 && !isCreating && (
        <div className="flex flex-col items-center justify-center py-10">
          <div className="w-full max-w-lg bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            {/* Card header */}
            <div className="flex items-center gap-2.5 px-6 py-4 border-b border-[var(--rule)]">
              <div className="w-7 h-7 flex items-center justify-center bg-[var(--slate-dim)]">
                <FolderOpen className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              </div>
              <span className="text-base font-semibold text-[var(--text-pri)]">Collections</span>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-sm text-[var(--text-ter)] leading-relaxed mb-5">
                A Collection is a flexible, named grouping of items for any purpose — thematic research, candidate shortlists, scope batches, or working sets. Unlike item pools, collections don't drive sessions. They're your scratchpad for organizing items before committing them to a cycle.
              </p>
              <div className="overflow-hidden">
                <DiagramCollections />
              </div>
            </div>

            {/* Footer — centered create button */}
            <div className="px-6 pb-6 flex justify-center">
              <button
                onClick={() => setIsCreating(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-[var(--slate)] text-white text-sm font-semibold hover:bg-[var(--slate)] shadow-sm shadow-indigo-200 transition-all"
              >
                <Plus className="w-4 h-4" />
                New Collection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inline create form when triggered with no existing collections */}
      {collections.length === 0 && isCreating && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 max-w-sm mx-auto">
          <p className="text-sm font-semibold text-[var(--text-pri)] mb-3">Create your first collection</p>
          <input
            ref={createInputRef}
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Collection name…"
            className="w-full px-3 py-1.5 text-sm border border-primary-300 bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-400 mb-3"
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleCreate();
              if (e.key === 'Escape') { setIsCreating(false); setNewName(''); }
            }}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={!newName.trim()}
              className="flex-1 px-3 py-1.5 text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => { setIsCreating(false); setNewName(''); }}
              className="px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {collections.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Left: collection list */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden lg:max-h-[calc(100vh-12rem)] flex flex-col">
          <div className="px-4 py-3 border-b border-[var(--rule)] flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--text-pri)]">Collections</h2>
            <button
              type="button"
              onClick={() => setIsCreating(true)}
              className="p-1 text-[var(--text-ter)] hover:text-primary-600 hover:bg-primary-50 transition-colors"
              title="New collection"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          {isCreating && (
            <div className="px-3 py-2.5 border-b border-[var(--rule)] bg-primary-50/50">
              <input
                ref={createInputRef}
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Collection name…"
                className="w-full px-3 py-1.5 text-sm border border-primary-300 bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-400 mb-2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleCreate();
                  if (e.key === 'Escape') { setIsCreating(false); setNewName(''); }
                }}
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleCreate()}
                  disabled={!newName.trim()}
                  className="flex-1 px-3 py-1.5 text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => { setIsCreating(false); setNewName(''); }}
                  className="px-3 py-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {collections.length === 0 && !isCreating ? (
            <div className="flex-1 flex flex-col items-center justify-center py-8 text-center px-4">
              <FolderOpen className="h-8 w-8 text-[var(--text-ter)] mb-3" />
              <p className="text-sm font-semibold text-[var(--text-pri)]">No collections yet</p>
              <p className="text-xs text-[var(--text-ter)] mt-1.5 leading-relaxed">
                Collections are flexible groupings of items for any purpose —<br/>
                research, themes, candidate shortlists, or anything else.
              </p>
              <button
                onClick={() => setIsCreating(true)}
                className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 hover:bg-primary-100 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Create your first collection
              </button>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-[var(--rule)]">
              {collections.map((collection) => {
                const isActive = collection.id === activeCollectionId;
                const count = itemCountByCollection.get(collection.id) ?? 0;
                return (
                  <button
                    key={collection.id}
                    type="button"
                    onClick={() => setActiveCollectionId(collection.id)}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      isActive ? 'bg-primary-50' : 'hover:bg-[var(--surface)]'
                    }`}
                  >
                    <div className={`font-medium text-sm truncate ${isActive ? 'text-primary-800' : 'text-[var(--text-pri)]'}`}>
                      {collection.name}
                    </div>
                    <div className="text-xs text-[var(--text-ter)] mt-0.5 flex items-center gap-2">
                      <span>{count} {count === 1 ? 'item' : 'items'}</span>
                      {count === 0 && <span className="text-[var(--text-ter)]">· empty</span>}
                    </div>
                    {collection.description && (
                      <div className="text-xs text-[var(--text-ter)] truncate mt-0.5">{collection.description}</div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: detail + scope analyzer */}
        <div className="min-w-0">
          {!activeCollection ? (
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-12 text-center">
              <FolderOpen className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
              <p className="text-sm font-medium text-[var(--text-sec)]">No collection selected</p>
              <p className="text-xs text-[var(--text-ter)] mt-1">Select a collection from the list, or create one.</p>
            </div>
          ) : (
            <CollectionDetail
              collection={activeCollection}
              orderedItemIds={activeOrderedItemIds}
              onRename={(name) => updateCollection(activeCollection.id, { name })}
              onDelete={() => handleDelete(activeCollection)}
              onAddItem={handleAddItem}
              onRemoveItem={handleRemoveItem}
              onReorder={handleReorder}
              onPromote={() => setPromoteModalOpen(true)}
            />
          )}
        </div>
        </div>
      )}

      {promoteModalOpen && activeCollection && (
        <PromoteToItemPoolModal
          collection={activeCollection}
          itemCount={activeResolvedItemCount}
          onConfirm={handlePromote}
          onClose={() => setPromoteModalOpen(false)}
          isLoading={isPromoting}
        />
      )}

      {/* In-app delete confirmation */}
      {pendingDeleteCollection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-base font-semibold text-[var(--text-pri)] mb-2">Delete collection?</h3>
            <p className="text-sm text-[var(--text-sec)] mb-5">
              <span className="font-medium">"{pendingDeleteCollection.name}"</span> will be removed. Items inside it will not be deleted.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setPendingDeleteCollection(null)}
                className="px-4 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium bg-[var(--coral)] text-white hover:bg-[var(--coral)] transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
